import { useEffect, useState, useRef } from 'react';
import { useRownd } from '@rownd/react';
import mapboxgl from 'mapbox-gl';
import { MAPBOX_ACCESS_TOKEN } from '../config/mapConfig';
import { 
  Box, 
  Button, 
  Paper, 
  Typography, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  TablePagination,
  Chip,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Grid,
  Divider,
  Card,
  CardMedia,
  CardContent,
  List,
  ListItem,
  ListItemText,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  OutlinedInput,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Checkbox,
  FormGroup,
  FormControlLabel,
  Switch,
  FormControlLabel as MuiFormControlLabel,
  ButtonGroup,
  Tooltip
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ImageIcon from '@mui/icons-material/Image';
import FilterListIcon from '@mui/icons-material/FilterList';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import WavesIcon from '@mui/icons-material/Waves';
import FlightIcon from '@mui/icons-material/Flight';
import RefreshIcon from '@mui/icons-material/Refresh';
import { getAllReports } from '../utils/api';

// Define the same popup styles as in SimpleMapTest
const popupStyle = `
.mapboxgl-popup {
  max-width: 400px;
  font: 12px/20px 'Helvetica Neue', Arial, Helvetica, sans-serif;
}

.mapboxgl-popup-content {
  padding: 15px;
  width: 280px; 
}

.popup-title {
  font-weight: bold;
  font-size: 16px;
  margin-bottom: 10px;
  border-bottom: 1px solid #ddd;
  padding-bottom: 5px;
}

.popup-row {
  margin-bottom: 5px;
}

.popup-label {
  font-weight: bold;
}
`;

// Inject styles
const injectCustomStyles = () => {
  const style = document.createElement('style');
  style.innerHTML = popupStyle;
  document.head.appendChild(style);
};

// Hardcoded tsunami data from SimpleMapTest
const TSUNAMI_DATA = [
  {
    timestamp: '2024-04-12 04:18:00',
    event_type: 'Earthquake',
    location_name: 'Epicenter',
    latitude: 16.65541,
    longitude: 116.096248,
    wave_height_m: 0,
    distance_km: 0,
    notes: 'Initial M8.3 earthquake detected'
  },
  {
    timestamp: '2024-04-12 04:20:55',
    event_type: 'Buoy Detection',
    location_name: 'Buoy-Central',
    latitude: 16.5,
    longitude: 116.4,
    wave_height_m: 3.43,
    distance_km: 36.7,
    notes: 'Wave detected by Buoy-Central'
  },
  {
    timestamp: '2024-04-12 04:26:21',
    event_type: 'Buoy Detection',
    location_name: 'Buoy-W01',
    latitude: 16.4,
    longitude: 115.3,
    wave_height_m: 1.8,
    distance_km: 89.51,
    notes: 'Wave detected by Buoy-W01'
  },
  {
    timestamp: '2024-04-12 04:27:00',
    event_type: 'Detection',
    location_name: 'Tsunami Warning Center',
    latitude: 16.85541,
    longitude: 116.246248,
    wave_height_m: 0.2,
    distance_km: 25,
    notes: 'Tsunami confirmed, warnings issued'
  }
];

// Predefined locations
const LOCATIONS = {
  tokyo: { center: [139.6503, 35.6762], zoom: 4, label: 'Tokyo, Japan' },
  manila: { center: [120.9842, 14.5995], zoom: 4, label: 'Manila, Philippines' },
  singapore: { center: [103.8198, 1.3521], zoom: 4, label: 'Singapore' },
  sydney: { center: [151.2093, -33.8688], zoom: 4, label: 'Sydney, Australia' }
};

// Add epicenter location
const TSUNAMI_EPICENTER = {
  center: [TSUNAMI_DATA[0].longitude, TSUNAMI_DATA[0].latitude],
  zoom: 4,
  label: 'Tsunami Epicenter'
};

// Add vessel data from SimpleMapTest
const VESSEL_DATA = [
  { id: "WCV-001", name: "MV Maxwell", lastCheckIn: "2025-04-10 14:36", status: "Operational", location: { lat: 11.176, lng: 111.061 }, safeHarbor: "Cam Ranh Bay Port" },
  { id: "WCV-002", name: "MV Young", lastCheckIn: "2025-04-10 15:52", status: "Damaged", location: { lat: 12.225, lng: 115.202 }, safeHarbor: "Scarborough Shoal Hold Area" },
  { id: "WCV-003", name: "MV Torres", lastCheckIn: "2025-04-10 17:36", status: "Operational", location: { lat: 10.51, lng: 112.183 }, safeHarbor: "Scarborough Shoal Hold Area" },
  { id: "WCV-004", name: "MV Johnson", lastCheckIn: "2025-04-10 15:04", status: "Anchored", location: { lat: 11.02, lng: 114.997 }, safeHarbor: "Unknown" },
  { id: "WCV-005", name: "MV Walker", lastCheckIn: "2025-04-10 16:29", status: "Diverted", location: { lat: 14.89, lng: 109.97 }, safeHarbor: "Subic Bay Terminal" },
  { id: "WCV-006", name: "MV Singh", lastCheckIn: "2025-04-10 14:05", status: "Operational", location: { lat: 14.065, lng: 112.058 }, safeHarbor: "Subic Bay Terminal" },
  { id: "WCV-007", name: "MV Alexander", lastCheckIn: "2025-04-10 15:45", status: "Diverted", location: { lat: 13.309, lng: 113.061 }, safeHarbor: "Cam Ranh Bay Port" },
  { id: "WCV-008", name: "MV Jones", lastCheckIn: "2025-04-10 16:40", status: "Awaiting Tow", location: { lat: 10.633, lng: 114.446 }, safeHarbor: "Subic Bay Terminal" },
  { id: "WCV-009", name: "MV Smith", lastCheckIn: "2025-04-10 15:16", status: "Anchored", location: { lat: 10.936, lng: 111.737 }, safeHarbor: "Subic Bay Terminal" },
  { id: "WCV-010", name: "MV Mayer", lastCheckIn: "2025-04-10 14:35", status: "Damaged", location: { lat: 13.092, lng: 116.855 }, safeHarbor: "Palawan Staging Anchorage" },
  { id: "WCV-011", name: "MV Jones", lastCheckIn: "2025-04-10 15:15", status: "Anchored", location: { lat: 13.7, lng: 113.946 }, safeHarbor: "Unknown" },
  { id: "WCV-012", name: "MV Henry", lastCheckIn: "2025-04-10 17:35", status: "Operational", location: { lat: 10.992, lng: 111.574 }, safeHarbor: "Cam Ranh Bay Port" },
  { id: "WCV-013", name: "MV Robertson", lastCheckIn: "2025-04-10 16:50", status: "Operational", location: { lat: 12.654, lng: 109.713 }, safeHarbor: "Palawan Staging Anchorage" },
  { id: "WCV-014", name: "MV Richardson", lastCheckIn: "2025-04-10 14:44", status: "Diverted", location: { lat: 11.415, lng: 112.464 }, safeHarbor: "Scarborough Shoal Hold Area" },
  { id: "WCV-015", name: "MV Wright", lastCheckIn: "2025-04-10 17:40", status: "Awaiting Tow", location: { lat: 13.973, lng: 110.976 }, safeHarbor: "Palawan Staging Anchorage" },
  { id: "WCV-016", name: "MV Rogers", lastCheckIn: "2025-04-10 14:25", status: "Diverted", location: { lat: 12.631, lng: 112.771 }, safeHarbor: "Unknown" },
  { id: "WCV-017", name: "MV Gonzales", lastCheckIn: "2025-04-10 14:59", status: "Awaiting Tow", location: { lat: 11.572, lng: 109.408 }, safeHarbor: "Unknown" },
  { id: "WCV-018", name: "MV Alexander", lastCheckIn: "2025-04-10 15:11", status: "Anchored", location: { lat: 10.152, lng: 110.255 }, safeHarbor: "Subic Bay Terminal" },
  { id: "WCV-019", name: "MV Yoder", lastCheckIn: "2025-04-10 14:52", status: "Damaged", location: { lat: 14.986, lng: 115.597 }, safeHarbor: "Scarborough Shoal Hold Area" },
  { id: "WCV-020", name: "MV Valenzuela", lastCheckIn: "2025-04-10 15:53", status: "Damaged", location: { lat: 13.562, lng: 114.899 }, safeHarbor: "Cam Ranh Bay Port" },
  { id: "WCV-021", name: "MV Brown", lastCheckIn: "2025-04-10 17:51", status: "Operational", location: { lat: 11.265, lng: 115.613 }, safeHarbor: "Cam Ranh Bay Port" },
  { id: "WCV-022", name: "MV Hill", lastCheckIn: "2025-04-10 16:39", status: "Diverted", location: { lat: 14.276, lng: 111.7 }, safeHarbor: "Unknown" },
  { id: "WCV-023", name: "MV Johnson", lastCheckIn: "2025-04-10 17:11", status: "Anchored", location: { lat: 12.507, lng: 109.84 }, safeHarbor: "Scarborough Shoal Hold Area" },
  { id: "WCV-024", name: "MV Dixon", lastCheckIn: "2025-04-10 17:02", status: "Diverted", location: { lat: 10.493, lng: 115.155 }, safeHarbor: "Scarborough Shoal Hold Area" },
  { id: "WCV-025", name: "MV Stanley", lastCheckIn: "2025-04-10 14:18", status: "Awaiting Tow", location: { lat: 12.81, lng: 111.176 }, safeHarbor: "Unknown" }
];

// Add Philippine airport data
const PHILIPPINE_AIRPORTS = [
  /*{ name: "Ninoy Aquino International Airport", latitude: 14.5086, longitude: 121.0198 },
  { name: "Mactan-Cebu International Airport", latitude: 10.3133, longitude: 123.9794 },
  { name: "Francisco Bangoy International Airport", latitude: 7.1255, longitude: 125.6458 },
  { name: "Clark International Airport", latitude: 15.186, longitude: 120.558 },
  { name: "Iloilo International Airport", latitude: 10.833, longitude: 122.493 },
  { name: "Kalibo International Airport", latitude: 11.687, longitude: 122.376 },
  { name: "Laoag International Airport", latitude: 18.178, longitude: 120.532 },
  { name: "Puerto Princesa International Airport", latitude: 9.742, longitude: 118.759 },
  { name: "Bacolod-Silay Airport", latitude: 10.776, longitude: 123.014 },
  { name: "Bohol-Panglao International Airport", latitude: 9.561, longitude: 123.775 },
  { name: "General Santos International Airport", latitude: 6.058, longitude: 125.096 },
  { name: "Laguindingan Airport", latitude: 8.612, longitude: 124.456 },
  { name: "Zamboanga International Airport", latitude: 6.922, longitude: 122.060 },*/
  // Add the three new airports
  { name: "NARIN INTL AIRPORT", code: "NIA", latitude: 16.04, longitude: 120.24 },
  { name: "BAYLOR AERIAL TERMINAL", code: "BAT", latitude: 15.19, longitude: 120.56 },
  { name: "OSTRA FLIGHT STRIP", code: "OFS", latitude: 13.11, longitude: 123.68 }
];

// Import cities and ports from SimpleMapTest.jsx file
const CITIES = [
  { id: 'CITY-001', name: 'Port Salinera', location: { lat: 16.04, lng: 120.3 } },
  { id: 'CITY-002', name: 'Coralspire City', location: { lat: 15.2, lng: 120.6 } },
  { id: 'CITY-003', name: 'New Dovarra', location: { lat: 16.4, lng: 120.6 } },
  { id: 'CITY-004', name: 'Lakara Gulf Point', location: { lat: 13.1, lng: 123.7 } },
  { id: 'CITY-005', name: 'Delmaris Township', location: { lat: 15.5, lng: 120.6 } }
];

// List of ports with coordinates
const PORTS = [
  { id: 'PORT-001', name: 'Port Salinera Port', location: { lat: 16.04, lng: 120.3 } },
  { id: 'PORT-002', name: 'Lakara Gulf Point Port', location: { lat: 13.1, lng: 123.7 } }
];

export default function AdminPage() {
  const { is_authenticated, getAccessToken } = useRownd();
  const [map, setMap] = useState(null);
  const [reports, setReports] = useState([]);
  const [filteredReports, setFilteredReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [count, setCount] = useState(0);
  const [timeframe, setTimeframe] = useState('');
  const mapContainer = useRef(null);
  const [selectedReport, setSelectedReport] = useState(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [apiBaseUrl, setApiBaseUrl] = useState('https://tsunami-data.robert-9e7.workers.dev');
  const [filtersExpanded, setFiltersExpanded] = useState(false);
  const [showTsunamiData, setShowTsunamiData] = useState(true);
  const [showVessels, setShowVessels] = useState(false);
  const [showAirports, setShowAirports] = useState(false);
  const [selectedVessel, setSelectedVessel] = useState(null);
  const [customLat, setCustomLat] = useState('');
  const [customLng, setCustomLng] = useState('');
  const [customZoom, setCustomZoom] = useState('10');
  const [popupPosition, setPopupPosition] = useState({ top: 0, left: 0 });
  
  // Filter states
  const [filters, setFilters] = useState({
    searchText: '',
    verified: 'all',
    hasImage: 'all',
    severity: 'all',
    dateRange: {
      startDate: '',
      endDate: ''
    },
    categories: []
  });

  // Track unique categories for filter options
  const [availableCategories, setAvailableCategories] = useState([]);

  // Convert tsunami data to GeoJSON format
  const createTsunamiGeoJson = (tsunamiData) => {
    if (!tsunamiData || tsunamiData.length === 0) {
      return {
        type: 'FeatureCollection',
        features: []
      };
    }
    
    return {
      type: 'FeatureCollection',
      features: tsunamiData.map((point, index) => ({
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: [point.longitude, point.latitude]
        },
        properties: {
          ...point,
          id: `tsunami-${index}`,
          title: point.location_name,
          description: point.notes,
          isTsunamiPoint: true
        }
      }))
    };
  };

  // Convert vessel data to GeoJSON format
  const createVesselGeoJson = (vesselData) => {
    if (!vesselData || vesselData.length === 0) {
      console.warn('No vessel data available for GeoJSON creation');
      return {
        type: 'FeatureCollection',
        features: []
      };
    }
    
    console.log('Creating vessel GeoJSON from', vesselData.length, 'vessels');
    
    const features = vesselData.map((vessel, index) => {
      // Generate a random bearing if none exists
      const bearing = vessel.bearing || Math.floor(Math.random() * 360);
      
      return {
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: [vessel.location.lng, vessel.location.lat]
        },
        properties: {
          ...vessel,
          id: vessel.id,
          title: vessel.name,
          description: `Status: ${vessel.status}`,
          isVesselPoint: true,
          bearing: bearing
        }
      };
    });
    
    console.log('Created GeoJSON features:', features.length);
    
    return {
      type: 'FeatureCollection',
      features: features
    };
  };

  // Add airport data to GeoJson
  const createAirportsGeoJson = (airportData) => ({
    type: 'FeatureCollection',
    features: airportData.map((airport, index) => ({
      type: 'Feature',
      geometry: {
        type: 'Point',
        coordinates: [airport.longitude, airport.latitude]
      },
      properties: {
        id: `airport-${index}`,
        title: airport.name
      }
    }))
  });

  // Convert cities data to GeoJSON format
  const createCitiesGeoJson = (citiesData) => {
    if (!citiesData || citiesData.length === 0) {
      return {
        type: 'FeatureCollection',
        features: []
      };
    }
    
    return {
      type: 'FeatureCollection',
      features: citiesData.map((city) => ({
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: [city.location.lng, city.location.lat]
        },
        properties: {
          ...city,
          title: city.name,
          description: `City: ${city.name}`,
          isCity: true
        }
      }))
    };
  };
  
  // Convert ports data to GeoJSON format
  const createPortsGeoJson = (portsData) => {
    if (!portsData || portsData.length === 0) {
      return {
        type: 'FeatureCollection',
        features: []
      };
    }
    
    return {
      type: 'FeatureCollection',
      features: portsData.map((port) => ({
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: [port.location.lng, port.location.lat]
        },
        properties: {
          ...port,
          title: port.name,
          description: `Port: ${port.name}`,
          isPort: true
        }
      }))
    };
  };

  // Initialize the map
  useEffect(() => {
    // Only create the map if authentication is complete
    if (is_authenticated === null) return;
    
    // Inject custom styles
    injectCustomStyles();
    
    // Set up the map
    mapboxgl.accessToken = MAPBOX_ACCESS_TOKEN;
    
    const mapInstance = new mapboxgl.Map({
      container: mapContainer.current,
      style: 'mapbox://styles/mapbox/streets-v11',
      center: [TSUNAMI_DATA[0].longitude, TSUNAMI_DATA[0].latitude], // Center on tsunami epicenter
      zoom: 4 // Zoomed out view of epicenter
    });
    
    // Add navigation controls
    mapInstance.addControl(new mapboxgl.NavigationControl(), 'top-right');
    
    // Handle missing icons by creating them on demand
    mapInstance.on('styleimagemissing', (e) => {
      const id = e.id; // id of the missing image
      console.log("Missing image:", id);
      
      // Create triangle for vessels if needed
      if (id === 'triangle-15') {
        // Create a custom triangle icon
        const width = 15;
        const height = 15;
        const data = new Uint8Array(width * height * 4);
        
        // Create a triangle shape
        const centerX = width / 2;
        const centerY = height / 2;
        
        // Define the triangle points
        const trianglePoints = [
          [centerX, centerY - height/3], // top
          [centerX - width/3, centerY + height/3], // bottom left
          [centerX + width/3, centerY + height/3]  // bottom right
        ];
        
        // Fill the triangle
        for (let x = 0; x < width; x++) {
          for (let y = 0; y < height; y++) {
            // Check if point is inside triangle
            const inside = pointInTriangle(
              x, y, 
              trianglePoints[0][0], trianglePoints[0][1],
              trianglePoints[1][0], trianglePoints[1][1],
              trianglePoints[2][0], trianglePoints[2][1]
            );
            
            const idx = (y * width + x) * 4;
            
            if (inside) {
              // White triangle
              data[idx] = 255;     // r
              data[idx + 1] = 255; // g
              data[idx + 2] = 255; // b
              data[idx + 3] = 255; // a
            } else {
              // Transparent
              data[idx] = 0;
              data[idx + 1] = 0;
              data[idx + 2] = 0;
              data[idx + 3] = 0;
            }
          }
        }
        
        // Add the image to the map
        mapInstance.addImage(id, { width, height, data });
        console.log("Added custom triangle image");
      }
    });
    
    // Helper function for triangle creation
    function pointInTriangle(px, py, ax, ay, bx, by, cx, cy) {
      const v0x = cx - ax;
      const v0y = cy - ay;
      const v1x = bx - ax;
      const v1y = by - ay;
      const v2x = px - ax;
      const v2y = py - ay;
      
      const dot00 = v0x * v0x + v0y * v0y;
      const dot01 = v0x * v1x + v0y * v1y;
      const dot02 = v0x * v2x + v0y * v2y;
      const dot11 = v1x * v1x + v1y * v1y;
      const dot12 = v1x * v2x + v1y * v2y;
      
      const invDenom = 1 / (dot00 * dot11 - dot01 * dot01);
      const u = (dot11 * dot02 - dot01 * dot12) * invDenom;
      const v = (dot00 * dot12 - dot01 * dot02) * invDenom;
      
      return u >= 0 && v >= 0 && u + v <= 1;
    }
    
    // Set up event handlers
    mapInstance.on('load', () => {
      console.log('Map loaded successfully');
      
      // Create vessel icon manually to ensure it's available
      const createShipIcon = () => {
        // Create different colored ship icons for each status
        const statuses = {
          'Operational': '#2ecc71', // Green for operational
          'Damaged': '#e74c3c',     // Red for damaged
          'Awaiting Tow': '#f39c12', // Orange for awaiting tow
          'Diverted': '#3498db',    // Blue for diverted
          'Anchored': '#9b59b6',    // Purple for anchored
          'In Transit': '#1abc9c',  // Turquoise for in transit
          'default': '#95a5a6'      // Grey default
        };
        
        // Create base ship icon
        const createColoredShipIcon = (color, name) => {
          const width = 24;  // Increased from 20 to 24
          const height = 24; // Increased from 20 to 24
          const data = new Uint8Array(width * height * 4);
          
          // Convert hex color to RGB
          const r = parseInt(color.substring(1, 3), 16);
          const g = parseInt(color.substring(3, 5), 16);
          const b = parseInt(color.substring(5, 7), 16);
          
          // Draw a simple ship/boat shape
          for (let y = 0; y < height; y++) {
            for (let x = 0; x < width; x++) {
              const idx = (y * width + x) * 4;
              
              // Create a boat shape (simplified triangle with flat bottom)
              const isShip = (
                // Center triangle
                (x >= width/4 && x <= width*3/4 && 
                 y >= height/4 && y <= height*3/4 && 
                 y >= (height/2 - (x - width/2) * 0.8) && 
                 y >= (height/2 + (x - width/2) * 0.8))
              );
              
              if (isShip) {
                // Fill with the status color
                data[idx] = r;      // r
                data[idx + 1] = g;  // g
                data[idx + 2] = b;  // b
                data[idx + 3] = 255; // a
              } else {
                // Transparent
                data[idx] = 0;
                data[idx + 1] = 0;
                data[idx + 2] = 0;
                data[idx + 3] = 0;
              }
            }
          }
          
          try {
            // Add a custom ship icon
            mapInstance.addImage(name, { width, height, data });
            console.log(`Created custom ship icon: ${name}`);
          } catch (error) {
            console.error(`Error creating ship icon ${name}:`, error);
          }
        };
        
        // Create a ship icon for each status
        for (const [status, color] of Object.entries(statuses)) {
          createColoredShipIcon(color, `ship-${status}`);
        }
        
        // Create a default ship icon
        createColoredShipIcon(statuses.default, 'ship-icon');
      };
      
      // Create the custom ship icon
      createShipIcon();
      
      // Add a GeoJSON source for the reports
      mapInstance.addSource('reports', {
        type: 'geojson',
        data: {
          type: 'FeatureCollection',
          features: []
        },
        cluster: true,
        clusterMaxZoom: 14,
        clusterRadius: 50
      });
      
      // Add clusters layer
      mapInstance.addLayer({
        id: 'clusters',
        type: 'circle',
        source: 'reports',
        filter: ['has', 'point_count'],
        paint: {
          'circle-color': [
            'step',
            ['get', 'point_count'],
            '#51bbd6',
            5,
            '#f1f075',
            10,
            '#f28cb1'
          ],
          'circle-radius': [
            'step',
            ['get', 'point_count'],
            20,
            5,
            30,
            10,
            40
          ]
        }
      });
      
      // Add cluster count layer
      mapInstance.addLayer({
        id: 'cluster-count',
        type: 'symbol',
        source: 'reports',
        filter: ['has', 'point_count'],
        layout: {
          'text-field': '{point_count_abbreviated}',
          'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
          'text-size': 12
        }
      });
      
      // Add individual points layer
      mapInstance.addLayer({
        id: 'unclustered-point',
        type: 'circle',
        source: 'reports',
        filter: ['!', ['has', 'point_count']],
        paint: {
          'circle-color': '#11b4da',
          'circle-radius': 8,
          'circle-stroke-width': 1,
          'circle-stroke-color': '#fff'
        }
      });
      
      // Add a GeoJSON source for tsunami data
      mapInstance.addSource('tsunami-points-source', {
        type: 'geojson',
        data: createTsunamiGeoJson(TSUNAMI_DATA)
      });
      
      // Add a circle layer for tsunami points
      mapInstance.addLayer({
        id: 'tsunami-points-layer',
        type: 'circle',
        source: 'tsunami-points-source',
        paint: {
          'circle-radius': [
            'interpolate',
            ['linear'],
            ['get', 'wave_height_m'],
            0, 3,
            1, 5,
            3, 8
          ],
          'circle-color': [
            'interpolate',
            ['linear'],
            ['get', 'wave_height_m'],
            0, '#3498db',    // Blue for minimal waves
            0.5, '#2ecc71',  // Green for small waves
            1, '#f39c12',    // Orange for medium waves
            2, '#e74c3c'     // Red for large waves
          ],
          'circle-stroke-width': 1.5,
          'circle-stroke-color': '#ffffff',
          'circle-opacity': 0.8
        },
        layout: {
          'visibility': 'none' // Hidden by default
        }
      });
      
      // Add a symbol layer for tsunami point text labels
      mapInstance.addLayer({
        id: 'tsunami-points-text',
        type: 'symbol',
        source: 'tsunami-points-source',
        layout: {
          'text-field': ['get', 'location_name'],
          'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
          'text-size': 12,
          'text-offset': [0, 1.5],
          'text-anchor': 'top',
          'visibility': 'none' // Hidden by default
        },
        paint: {
          'text-color': '#000000',
          'text-halo-color': '#ffffff',
          'text-halo-width': 1
        }
      });
      
      // Add tsunami radius circle source and layer
      const epicenterPoint = TSUNAMI_DATA.find(data => data.event_type === 'Earthquake');
      if (epicenterPoint) {
        // Add tsunami wave propagation circles (30, 45, 60 min)
        // Create 250 km circle - ~30 min propagation at 500 km/h
        mapInstance.addSource('tsunami-wave-30min', {
          type: 'geojson',
          data: {
            type: 'FeatureCollection',
            features: [createTsunamiRadiusCircle(epicenterPoint, 250)]
          }
        });
        
        mapInstance.addLayer({
          id: 'tsunami-wave-30min-circle',
          type: 'line',
          source: 'tsunami-wave-30min',
          layout: {
            'visibility': 'none', // Hidden by default
            'line-join': 'round',
            'line-cap': 'round'
          },
          paint: {
            'line-color': 'rgba(0, 128, 0, 0.8)',
            'line-width': 2,
            'line-opacity': 0.7
          }
        });
        
        // Create 375 km circle - ~45 min propagation
        mapInstance.addSource('tsunami-wave-45min', {
          type: 'geojson',
          data: {
            type: 'FeatureCollection',
            features: [createTsunamiRadiusCircle(epicenterPoint, 375)]
          }
        });
        
        mapInstance.addLayer({
          id: 'tsunami-wave-45min-circle',
          type: 'line',
          source: 'tsunami-wave-45min',
          layout: {
            'visibility': 'none', // Hidden by default
            'line-join': 'round',
            'line-cap': 'round'
          },
          paint: {
            'line-color': 'rgba(255, 255, 0, 0.8)',
            'line-width': 2,
            'line-opacity': 0.7
          }
        });
        
        // Create 500 km circle - ~60 min propagation
        mapInstance.addSource('tsunami-wave-60min', {
          type: 'geojson',
          data: {
            type: 'FeatureCollection',
            features: [createTsunamiRadiusCircle(epicenterPoint, 500)]
          }
        });
        
        mapInstance.addLayer({
          id: 'tsunami-wave-60min-circle',
          type: 'line',
          source: 'tsunami-wave-60min',
          layout: {
            'visibility': 'none', // Hidden by default
            'line-join': 'round',
            'line-cap': 'round'
          },
          paint: {
            'line-color': 'rgba(255, 0, 0, 0.8)',
            'line-width': 2,
            'line-opacity': 0.7
          }
        });
        
        // Add a GeoJSON source for the tsunami radius
        mapInstance.addSource('tsunami-radius-source', {
          type: 'geojson',
          data: {
            type: 'FeatureCollection',
            features: [createTsunamiRadiusCircle(epicenterPoint, 1000)] // 1000km radius
          }
        });
        
        // Add a line layer for the tsunami radius
        mapInstance.addLayer({
          id: 'tsunami-radius-circle',
          type: 'line',
          source: 'tsunami-radius-source',
          layout: {
            'visibility': 'none', // Hidden by default
            'line-join': 'round',
            'line-cap': 'round'
          },
          paint: {
            'line-color': '#e74c3c',
            'line-width': 2,
            'line-opacity': 0.7,
            'line-dasharray': [2, 2]
          }
        });
        
        // Add a symbol layer for the tsunami radius label
        mapInstance.addLayer({
          id: 'tsunami-radius-label',
          type: 'symbol',
          source: 'tsunami-radius-source',
          layout: {
            'text-field': '1000km Impact Radius',
            'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
            'text-size': 12,
            'text-offset': [0, -2],
            'text-anchor': 'top',
            'symbol-placement': 'line-center',
            'visibility': 'none' // Hidden by default
          },
          paint: {
            'text-color': '#e74c3c',
            'text-halo-color': '#ffffff',
            'text-halo-width': 1.5
          }
        });
      }
      
      // Handle clicks on tsunami points
      mapInstance.on('click', 'tsunami-points-layer', (e) => {
        // Prevent the default behavior
        e.preventDefault();
        // No popup will be shown for tsunami data points
      });
      
      // Change cursor on hover for tsunami points
      mapInstance.on('mouseenter', 'tsunami-points-layer', () => {
        mapInstance.getCanvas().style.cursor = 'pointer';
      });
      
      mapInstance.on('mouseleave', 'tsunami-points-layer', () => {
        mapInstance.getCanvas().style.cursor = '';
      });
      
      // Handle clicks on unclustered points
      mapInstance.on('click', 'unclustered-point', (e) => {
        const coordinates = e.features[0].geometry.coordinates.slice();
        const properties = e.features[0].properties;
        
        // Create popup content
        const popupContent = `
          <div class="popup-title">${properties.title || 'Tsunami Report'}</div>
          <div class="popup-row"><span class="popup-label">ID:</span> ${properties.id.substring(0, 8)}...</div>
          <div class="popup-row"><span class="popup-label">Location:</span> ${properties.latitude}, ${properties.longitude}</div>
          <div class="popup-row"><span class="popup-label">Date:</span> ${new Date(properties.reported_at).toLocaleString()}</div>
          <div class="popup-row"><span class="popup-label">Severity:</span> ${properties.severity || 'N/A'}</div>
          <div class="popup-row"><span class="popup-label">Verified:</span> ${properties.verified ? 'Yes' : 'No'}</div>
        `;
        
        // Ensure that if the map is zoomed out such that multiple
        // copies of the feature are visible, the popup appears
        // over the copy being pointed to.
        while (Math.abs(e.lngLat.lng - coordinates[0]) > 180) {
          coordinates[0] += e.lngLat.lng > coordinates[0] ? 360 : -360;
        }
        
        new mapboxgl.Popup()
          .setLngLat(coordinates)
          .setHTML(popupContent)
          .addTo(mapInstance);
      });
      
      // Add zoom handler to update the tsunami radius circle
      mapInstance.on('zoomend', () => {
        updateEpicenterRadiusCircle(mapInstance);
      });
      
      // Add moveend handler to update the tsunami radius circle
      mapInstance.on('moveend', () => {
        updateEpicenterRadiusCircle(mapInstance);
      });
      
      // Add a GeoJSON source for vessel data
      mapInstance.addSource('vessels-source', {
        type: 'geojson',
        data: createVesselGeoJson(VESSEL_DATA)
      });
      
      // Add a symbol layer for vessels
      mapInstance.addLayer({
        id: 'vessels-layer',
        type: 'symbol',
        source: 'vessels-source',
        layout: {
          'icon-image': [
            'case',
            ['==', ['get', 'status'], 'Operational'], 'ship-Operational',
            ['==', ['get', 'status'], 'Damaged'], 'ship-Damaged',
            ['==', ['get', 'status'], 'Awaiting Tow'], 'ship-Awaiting Tow',
            ['==', ['get', 'status'], 'Diverted'], 'ship-Diverted',
            ['==', ['get', 'status'], 'Anchored'], 'ship-Anchored',
            ['==', ['get', 'status'], 'In Transit'], 'ship-In Transit',
            'ship-icon' // default
          ],
          'icon-size': 1.5, // Increased from 1.0 to 1.5
          'icon-allow-overlap': true,
          'icon-rotate': ['get', 'bearing'],
          'text-field': ['get', 'name'],
          'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
          'text-size': 10,
          'text-offset': [0, 1.5],
          'text-anchor': 'top',
          'visibility': 'none' // Hidden by default
        },
        paint: {
          'text-color': '#000000',
          'text-halo-color': '#ffffff',
          'text-halo-width': 1
        }
      });
      
      // Handle clicks on vessel points
      mapInstance.on('click', 'vessels-layer', (e) => {
        try {
          const features = mapInstance.queryRenderedFeatures(e.point, { 
            layers: ['vessels-layer'] 
          });
          
          if (features.length > 0) {
            const feature = features[0];
            const properties = feature.properties;
            const vessel = VESSEL_DATA.find(v => v.id === properties.id);
            
            if (vessel) {
              // Get the vessel's coordinates and project them to screen coordinates
              const coordinates = [vessel.location.lng, vessel.location.lat];
              const point = mapInstance.project(coordinates);
              
              console.log('Vessel clicked:', vessel.name, 'at screen position:', point);
              
              setSelectedVessel(vessel);
              setPopupPosition({ left: point.x, top: point.y });
              setSelectedReport(null);
              setSelectedTsunamiPoint(null);
            }
          }
        } catch (error) {
          console.error('Error creating vessel popup:', error);
        }
      });
      
      // Change cursor on hover for vessels
      mapInstance.on('mouseenter', 'vessels-layer', () => {
        mapInstance.getCanvas().style.cursor = 'pointer';
      });
      
      mapInstance.on('mouseleave', 'vessels-layer', () => {
        mapInstance.getCanvas().style.cursor = '';
      });
      
      // Add airport source
      mapInstance.addSource('airport-source', {
        type: 'geojson',
        data: createAirportsGeoJson(PHILIPPINE_AIRPORTS)
      });

      // Airport icons
      mapInstance.addLayer({
        id: 'airport-layer',
        type: 'symbol',
        source: 'airport-source',
        layout: {
          'icon-image': 'airport-15', // built-in icon
          'icon-size': 1.2,
          'icon-allow-overlap': true,
          'visibility': 'none', // Hidden by default
        },
      });

      // Airport labels
      mapInstance.addLayer({
        id: 'airport-labels',
        type: 'symbol',
        source: 'airport-source',
        layout: {
          'text-field': ['get', 'title'],
          'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
          'text-size': 11,
          'text-offset': [0, 1.2],
          'text-anchor': 'top',
          'visibility': 'none', // Hidden by default
        },
        paint: {
          'text-color': '#003366',
          'text-halo-color': '#ffffff',
          'text-halo-width': 1
        }
      });

      // Add a click handler for airports
      mapInstance.on('click', 'airport-layer', (e) => {
        try {
          const features = mapInstance.queryRenderedFeatures(e.point, { 
            layers: ['airport-layer'] 
          });
          
          if (features.length > 0) {
            const feature = features[0];
            const coordinates = feature.geometry.coordinates.slice();
            const properties = feature.properties;
            
            // Create popup content for airport
            const popupContent = `
              <div class="popup-title">${properties.title}</div>
              <div class="popup-row"><span class="popup-label">Location:</span> ${coordinates[1].toFixed(4)}, ${coordinates[0].toFixed(4)}</div>
            `;
            
            // Ensure that if the map is zoomed out such that multiple
            // copies of the feature are visible, the popup appears
            // over the copy being pointed to.
            while (Math.abs(e.lngLat.lng - coordinates[0]) > 180) {
              coordinates[0] += e.lngLat.lng > coordinates[0] ? 360 : -360;
            }
            
            new mapboxgl.Popup()
              .setLngLat(coordinates)
              .setHTML(popupContent)
              .addTo(mapInstance);
          }
        } catch (error) {
          console.error('Error creating airport popup:', error);
        }
      });
      
      // Change cursor on hover for airports
      mapInstance.on('mouseenter', 'airport-layer', () => {
        mapInstance.getCanvas().style.cursor = 'pointer';
      });
      
      mapInstance.on('mouseleave', 'airport-layer', () => {
        mapInstance.getCanvas().style.cursor = '';
      });
      
      // Add cities data source
      mapInstance.addSource('cities-source', {
        type: 'geojson',
        data: createCitiesGeoJson(CITIES)
      });
      
      // Add ports data source
      mapInstance.addSource('ports-source', {
        type: 'geojson',
        data: createPortsGeoJson(PORTS)
      });
      
      // Add icons for cities and ports
      Promise.all([
        new Promise((resolve, reject) => {
          mapInstance.loadImage(
            'https://img.icons8.com/ios-filled/50/000000/city.png',
            (error, image) => {
              if (error) {
                console.error('Failed to load city icon:', error);
                reject(error);
        return;
      }
              mapInstance.addImage('city-icon', image);
              resolve();
            }
          );
        }),
        new Promise((resolve, reject) => {
          mapInstance.loadImage(
            'https://img.icons8.com/ios-filled/50/000000/anchor.png',
            (error, image) => {
              if (error) {
                console.error('Failed to load port icon:', error);
                reject(error);
                return;
              }
              mapInstance.addImage('port-icon', image);
              resolve();
            }
          );
        })
      ]).then(() => {
        // Add cities layer once icons are loaded
        mapInstance.addLayer({
          id: 'cities-layer',
          type: 'symbol',
          source: 'cities-source',
          layout: {
            'icon-image': 'city-icon',
            'icon-size': 0.5,
            'icon-allow-overlap': true,
            'visibility': 'visible' // Always visible
          }
        });
        
        // Add a text layer for city names
        mapInstance.addLayer({
          id: 'cities-text',
          type: 'symbol',
          source: 'cities-source',
          layout: {
            'text-field': ['get', 'title'],
            'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
            'text-size': 10,
            'text-offset': [0, 1.5],
            'text-anchor': 'top',
            'text-allow-overlap': false,
            'visibility': 'visible' // Always visible
          },
          paint: {
            'text-color': '#673ab7', // Purple color for cities
            'text-halo-color': '#ffffff',
            'text-halo-width': 1
          }
        });
        
        // Add ports layer
        mapInstance.addLayer({
          id: 'ports-layer',
          type: 'symbol',
          source: 'ports-source',
          layout: {
            'icon-image': 'port-icon',
            'icon-size': 0.5,
            'icon-allow-overlap': true,
            'visibility': 'visible' // Always visible
          }
        });
        
        // Add a text layer for port names
        mapInstance.addLayer({
          id: 'ports-text',
          type: 'symbol',
          source: 'ports-source',
          layout: {
            'text-field': ['get', 'title'],
            'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
            'text-size': 10,
            'text-offset': [0, 1.5],
            'text-anchor': 'top',
            'text-allow-overlap': false,
            'visibility': 'visible' // Always visible
          },
          paint: {
            'text-color': '#009688', // Teal color for ports
            'text-halo-color': '#ffffff',
            'text-halo-width': 1
          }
        });
      }).catch(err => {
        console.error('Error loading map icons:', err);
      });
      
      // Save map instance to state
      setMap(mapInstance);
      
      // Fly to epicenter when map loads - with additional check
      setTimeout(() => {
        try {
          // Check if all needed sources/layers are initialized first
          if (
            mapInstance && 
            mapInstance.loaded() && 
            mapInstance.getSource('tsunami-radius-source') &&
            mapInstance.getLayer('tsunami-radius-circle')
          ) {
            // Use mapInstance directly since the state might not be updated yet
            const epicenterLocation = TSUNAMI_EPICENTER.center;
            
            mapInstance.flyTo({
              center: epicenterLocation,
              zoom: 6,
              essential: true,
              duration: 2000
            });
          } else {
            console.log('Map not fully loaded yet, delaying flyTo');
            // Try again in another second if not ready
            setTimeout(() => {
              try {
                if (mapInstance && mapInstance.loaded()) {
                  mapInstance.flyTo({
                    center: TSUNAMI_EPICENTER.center,
                    zoom: 6,
                    essential: true,
                    duration: 2000
                  });
                }
              } catch (error) {
                console.warn('Delayed flyTo failed:', error.message);
              }
            }, 1000);
          }
        } catch (error) {
          console.warn('Error in initial flyTo operation:', error.message);
        }
      }, 1000); // Short delay to ensure map is ready
    });
    
    // Cleanup function
    return () => {
      if (mapInstance) {
        mapInstance.remove();
      }
    };
  }, [is_authenticated]);
  
  // Toggle tsunami data visibility
  useEffect(() => {
    if (map) {
      const visibility = showTsunamiData ? 'visible' : 'none';
      map.setLayoutProperty('tsunami-points-layer', 'visibility', visibility);
      map.setLayoutProperty('tsunami-points-text', 'visibility', visibility);
      
      // Also toggle the tsunami radius circle and label
      if (map.getLayer('tsunami-radius-circle')) {
        map.setLayoutProperty('tsunami-radius-circle', 'visibility', visibility);
      }
      
      if (map.getLayer('tsunami-radius-label')) {
        map.setLayoutProperty('tsunami-radius-label', 'visibility', visibility);
      }
      
      // Toggle propagation circles visibility
      if (map.getLayer('tsunami-wave-30min-circle')) {
        map.setLayoutProperty('tsunami-wave-30min-circle', 'visibility', visibility);
      }
      
      if (map.getLayer('tsunami-wave-45min-circle')) {
        map.setLayoutProperty('tsunami-wave-45min-circle', 'visibility', visibility);
      }
      
      if (map.getLayer('tsunami-wave-60min-circle')) {
        map.setLayoutProperty('tsunami-wave-60min-circle', 'visibility', visibility);
      }
      
      // If showing tsunami data, fly to an appropriate location
      if (showTsunamiData) {
        // Find the epicenter (first point) in the tsunami data
        const epicenter = TSUNAMI_DATA.find(data => data.event_type === 'Earthquake');
        if (epicenter) {
          map.flyTo({
            center: [epicenter.longitude, epicenter.latitude],
            zoom: 6,
            essential: true,
            duration: 2000
          });
        }
      }
    }
  }, [showTsunamiData, map]);
  
  // Toggle vessel display
  const toggleVessels = () => {
    const newShowVessels = !showVessels;
    setShowVessels(newShowVessels);
    
    if (map) {
      console.log('Toggling vessels to:', newShowVessels ? 'visible' : 'none');
      // Update vessel layer visibility
      map.setLayoutProperty('vessels-layer', 'visibility', newShowVessels ? 'visible' : 'none');
      
      // Force vessels to be visible by reloading data - useful for debugging
      if (newShowVessels) {
        try {
          const source = map.getSource('vessels-source');
          if (source) {
            const data = createVesselGeoJson(VESSEL_DATA);
            console.log('Refreshing vessel data:', data);
            source.setData(data);
          }
        } catch (error) {
          console.error('Error refreshing vessel data:', error);
        }
      }
    }
  };
  
  // Calculate point on a circle at given bearing and distance
  const calculateDestination = (lat, lng, bearing, distance) => {
    // Earth's radius in kilometers
    const R = 6371;
    
    // Convert to radians
    const lat1 = lat * Math.PI / 180;
    const lng1 = lng * Math.PI / 180;
    const brng = bearing * Math.PI / 180;
    
    // Calculate
    const d = distance / R;
    
    let lat2 = Math.asin(
      Math.sin(lat1) * Math.cos(d) + Math.cos(lat1) * Math.sin(d) * Math.cos(brng)
    );
    
    let lng2 = lng1 + Math.atan2(
      Math.sin(brng) * Math.sin(d) * Math.cos(lat1),
      Math.cos(d) - Math.sin(lat1) * Math.sin(lat2)
    );
    
    // Convert back to degrees
    lat2 = lat2 * 180 / Math.PI;
    lng2 = lng2 * 180 / Math.PI;
    
    return [lat2, lng2];
  };

  // Function to create tsunami impact radius circle
  const createTsunamiRadiusCircle = (epicenter, radiusKm = 1000) => {
    const points = [];
    const numPoints = 64; // Number of points to make the circle smooth
    
    for (let i = 0; i < numPoints; i++) {
      const bearing = (i * 360) / numPoints;
      const [lat, lng] = calculateDestination(
        epicenter.latitude,
        epicenter.longitude,
        bearing,
        radiusKm
      );
      points.push([lng, lat]);
    }
    
    // Close the circle
    points.push(points[0]);
    
    return {
      type: 'Feature',
      geometry: {
        type: 'LineString',
        coordinates: points
      },
      properties: {
        description: `${radiusKm}km impact radius`
      }
    };
  };

  // Update epicenter circle based on zoom level
  const updateEpicenterRadiusCircle = (mapInstance) => {
    // Check if map instance exists
    if (!mapInstance) return;
    
    // Check if the map is fully loaded
    if (!mapInstance.loaded()) return;
    
    // Check if the source exists before proceeding
    try {
      const source = mapInstance.getSource('tsunami-radius-source');
      if (!source) {
        console.log('tsunami-radius-source not initialized yet');
        return;
      }
      
      const epicenter = TSUNAMI_EPICENTER.center; // [lng, lat]
      const center = { lng: epicenter[0], lat: epicenter[1] };
      const zoom = mapInstance.getZoom();
      
      // Calculate radius in pixels at current zoom level (constant screen size)
      const radiusPixels = 100; // Fixed size in pixels
      
      // Calculate ground resolution at this latitude and zoom level
      // Based on Mapbox's pixel to meter formula
      const groundResolution = 156543.03392 * Math.cos(center.lat * Math.PI / 180) / Math.pow(2, zoom);
      
      // Convert pixels to kilometers
      const radiusKm = (radiusPixels * groundResolution) / 1000;
      
      // Update the source data
      source.setData({
        type: 'FeatureCollection',
        features: [createTsunamiRadiusCircle(epicenter, radiusKm)]
      });
    } catch (error) {
      console.warn('Error updating epicenter radius circle:', error.message);
    }
  };
  
  // Load reports data
  useEffect(() => {
    let isMounted = true;
    
    async function loadReports() {
      if (!map) return;
      
      setLoading(true);
      setError(null);
      
      try {
        // Fetch reports data
        const result = await getAllReports();
        
        if (isMounted) {
          const reportsData = result.reports || [];
          // Update state with reports data
          setReports(reportsData);
          setFilteredReports(reportsData);
          setCount(reportsData.length || 0);
          setTimeframe(result.timeframe || '');
          
          // Extract unique categories for filters
          const categories = new Set();
          reportsData.forEach(report => {
            if (report.ai_categories && Array.isArray(report.ai_categories)) {
              report.ai_categories.forEach(cat => {
                if (cat.category) {
                  categories.add(cat.category);
                }
              });
            }
          });
          setAvailableCategories(Array.from(categories));
          
          // Update map data
          if (map && reportsData && reportsData.length > 0) {
            // Convert reports to GeoJSON
            const features = reportsData
              .filter(report => report.latitude !== null && report.longitude !== null)
              .map(report => ({
                type: 'Feature',
                geometry: {
                  type: 'Point',
                  coordinates: [report.longitude, report.latitude]
                },
                properties: {
                  id: report.id,
                  title: report.title || report.text || 'Unnamed Report',
                  description: report.description || report.text || 'No description',
                  severity: report.severity,
                  reported_at: report.reported_at,
                  created_at: report.created_at,
                  verified: report.verified ? 1 : 0,
                  latitude: report.latitude,
                  longitude: report.longitude
                }
              }));
            
            // Update the source data
            const source = map.getSource('reports');
            if (source) {
              source.setData({
                type: 'FeatureCollection',
                features
              });
            }
            
            // Auto fly to first report if available
            // if (features.length > 0) {
            //   map.flyTo({
            //     center: features[0].geometry.coordinates,
            //     zoom: 5,
            //     essential: true,
            //     duration: 2000
            //   });
            // }
            }
          }
        } catch (error) {
        if (isMounted) {
          console.error('Error loading reports:', error);
          setError('Failed to load reports. Please try again later.');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    }
    
    loadReports();
    
    return () => {
      isMounted = false;
    };
  }, [map, is_authenticated]);
  
  // Filter reports when filter values change
  useEffect(() => {
    const applyFilters = () => {
      let result = [...reports];
      
      // Filter by search text (search in title, description, id)
      if (filters.searchText) {
        const searchLower = filters.searchText.toLowerCase();
        result = result.filter(report => 
          (report.title?.toLowerCase().includes(searchLower) || false) ||
          (report.description?.toLowerCase().includes(searchLower) || false) ||
          (report.text?.toLowerCase().includes(searchLower) || false) ||
          (report.id?.toLowerCase().includes(searchLower) || false) ||
          (report.location_description?.toLowerCase().includes(searchLower) || false)
        );
      }
      
      // Filter by verification status
      if (filters.verified !== 'all') {
        const isVerified = filters.verified === 'verified';
        result = result.filter(report => Boolean(report.verified) === isVerified);
      }
      
      // Filter by image presence
      if (filters.hasImage !== 'all') {
        const hasImage = filters.hasImage === 'has_image';
        result = result.filter(report => 
          hasImage 
            ? (report.image_urls && report.image_urls.length > 0) 
            : (!report.image_urls || report.image_urls.length === 0)
        );
      }
      
      // Filter by severity
      if (filters.severity !== 'all') {
        result = result.filter(report => report.severity === filters.severity);
      }
      
      // Filter by date range
      if (filters.dateRange.startDate) {
        const startDate = new Date(filters.dateRange.startDate);
        result = result.filter(report => {
          const reportDate = new Date(report.reported_at);
          return reportDate >= startDate;
        });
      }
      
      if (filters.dateRange.endDate) {
        const endDate = new Date(filters.dateRange.endDate);
        // Set end date to end of day
        endDate.setHours(23, 59, 59, 999);
        result = result.filter(report => {
          const reportDate = new Date(report.reported_at);
          return reportDate <= endDate;
        });
      }
      
      // Filter by categories
      if (filters.categories.length > 0) {
        result = result.filter(report => {
          if (!report.ai_categories || !Array.isArray(report.ai_categories)) {
            return false;
          }
          
          return report.ai_categories.some(cat => 
            filters.categories.includes(cat.category)
          );
        });
      }
      
      setFilteredReports(result);
      setCount(result.length);
      setPage(0);
    };
    
    applyFilters();
  }, [filters, reports]);
  
  // Handle page change
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };
  
  // Handle rows per page change
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  
  // Format date in a human-readable format
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    
    try {
      const date = new Date(dateString);
      return date.toLocaleString();
    } catch (error) {
      return dateString;
    }
  };
  
  // Refresh data
  const handleRefresh = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const result = await getAllReports();
      const reportsData = result.reports || [];
      
      setReports(reportsData);
      setCount(reportsData.length || 0);
      setTimeframe(result.timeframe || '');
      
      // Extract unique categories for filters
      const categories = new Set();
      reportsData.forEach(report => {
        if (report.ai_categories && Array.isArray(report.ai_categories)) {
          report.ai_categories.forEach(cat => {
            if (cat.category) {
              categories.add(cat.category);
            }
          });
        }
      });
      setAvailableCategories(Array.from(categories));
      
      // Update map data
      if (map && reportsData && reportsData.length > 0) {
        // Convert reports to GeoJSON
        const features = reportsData
          .filter(report => report.latitude !== null && report.longitude !== null)
          .map(report => ({
            type: 'Feature',
            geometry: {
              type: 'Point',
              coordinates: [report.longitude, report.latitude]
            },
            properties: {
              id: report.id,
              title: report.title || report.text || 'Unnamed Report',
              description: report.description || report.text || 'No description',
              severity: report.severity,
              reported_at: report.reported_at,
              created_at: report.created_at,
              verified: report.verified ? 1 : 0,
              latitude: report.latitude,
              longitude: report.longitude
            }
          }));
        
        // Update the source data
        const source = map.getSource('reports');
        if (source) {
          source.setData({
            type: 'FeatureCollection',
            features
          });
        }
      }
    } catch (error) {
      console.error('Error refreshing reports:', error);
      setError('Failed to refresh reports. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  // Handle opening the detail dialog
  const handleRowClick = (report) => {
    setSelectedReport(report);
    setDetailDialogOpen(true);
  };

  // Handle closing the detail dialog
  const handleCloseDetailDialog = () => {
    setDetailDialogOpen(false);
  };

  // Format JSON data for display
  const formatJsonData = (data) => {
    if (!data) return 'None';
    
    try {
      if (typeof data === 'string') {
        return JSON.stringify(JSON.parse(data), null, 2);
      }
      return JSON.stringify(data, null, 2);
    } catch (error) {
      return String(data);
    }
  };
  
  // Handle filter changes
  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };
  
  // Handle date range filter changes
  const handleDateRangeChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      dateRange: {
        ...prev.dateRange,
        [field]: value
      }
    }));
  };
  
  // Handle category filter change
  const handleCategoryChange = (category) => {
    setFilters(prev => {
      const categories = [...prev.categories];
      
      if (categories.includes(category)) {
        return {
          ...prev,
          categories: categories.filter(cat => cat !== category)
        };
      } else {
        return {
          ...prev,
          categories: [...categories, category]
        };
      }
    });
  };
  
  // Reset all filters
  const resetFilters = () => {
    setFilters({
      searchText: '',
      verified: 'all',
      hasImage: 'all',
      severity: 'all',
      dateRange: {
        startDate: '',
        endDate: ''
      },
      categories: []
    });
    setFilteredReports(reports);
    setCount(reports.length);
  };
  
  // Get unique severity values
  const severityOptions = ['low', 'medium', 'high', ...new Set(reports.map(r => r.severity).filter(Boolean))];
  
  // Fly to tsunami epicenter
  const flyToTsunamiEpicenter = () => {
    if (map) {
      flyToLocation(TSUNAMI_EPICENTER.center[0], TSUNAMI_EPICENTER.center[1], TSUNAMI_EPICENTER.zoom);
    }
  };

  // Fly to predefined location
  const flyToPredefinedLocation = (locationKey) => {
    const location = LOCATIONS[locationKey];
    if (location && map) {
      flyToLocation(location.center[0], location.center[1], location.zoom);
    }
  };

  // Change map view to specific location
  const flyToLocation = (longitude, latitude, zoom = 10) => {
    if (map) {
      map.flyTo({
        center: [longitude, latitude],
        zoom: zoom,
        essential: true,
        duration: 2000 // Animation time in milliseconds
      });
    }
  };
  
  // Toggle airport display
  const toggleAirports = () => {
    const newShowAirports = !showAirports;
    setShowAirports(newShowAirports);
    
    if (map) {
      // Update airport layer visibility
      map.setLayoutProperty('airport-layer', 'visibility', newShowAirports ? 'visible' : 'none');
      map.setLayoutProperty('airport-labels', 'visibility', newShowAirports ? 'visible' : 'none');
    }
  };
  
  return (
    <Box>
      <Typography variant="h4" gutterBottom sx={{ fontSize: { xs: '1.5rem', sm: '2rem' } }}>
        Admin Dashboard
      </Typography>
      
      {/* Map View with Tsunami Toggle */}
      <Paper sx={{ p: { xs: 1, sm: 2 }, mb: 2 }}>
        <Box sx={{ 
          display: 'flex', 
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between', 
          mb: 2 
        }}>
          <Typography variant="h6" sx={{ mb: { xs: 1, sm: 0 } }}>
            Map View
          </Typography>
          <Box sx={{ 
            display: 'flex', 
            flexDirection: { xs: 'column', sm: 'row' }, 
            gap: 1 
          }}>
            <Button 
              variant="contained" 
              color={showTsunamiData ? "primary" : "secondary"}
              onClick={() => setShowTsunamiData(!showTsunamiData)}
              fullWidth={{ xs: true, sm: false }}
              size="small"
              sx={{ mr: { sm: 1 } }}
            >
              {showTsunamiData ? "Hide Tsunami Data" : "Show Tsunami Data"}
            </Button>
            <Button 
              variant="contained" 
              color={showVessels ? "primary" : "secondary"}
              onClick={toggleVessels}
              fullWidth={{ xs: true, sm: false }}
              size="small"
              sx={{ mr: { sm: 1 } }}
            >
              {showVessels ? "Hide Vessels" : "Show Vessels"}
            </Button>
            <Button 
              variant="contained" 
              color={showAirports ? "primary" : "secondary"}
              onClick={toggleAirports}
              startIcon={<FlightIcon />}
              fullWidth={{ xs: true, sm: false }}
              size="small"
            >
              {showAirports ? "Hide Airports" : "Show Airports"}
            </Button>
          </Box>
        </Box>
        
        <Box 
          ref={mapContainer} 
          sx={{ 
            height: { xs: '300px', sm: '400px', md: '500px' }, 
            width: '100%', 
            position: 'relative',
            border: '1px solid #ddd', 
            borderRadius: '4px'
          }}
        ></Box>
        
        {/* Custom Popup for Vessels */}
        {selectedVessel && (
          <Card
            className="popup-card"
            sx={{
              position: 'absolute',
              left: `${popupPosition.left}px`,
              top: `${popupPosition.top}px`,
              transform: 'translate(-50%, -100%)', // Center horizontally and position above the point
              width: 240,
              maxWidth: '90%',
              zIndex: 10,
              boxShadow: 3,
              mt: -2, // Shift up slightly to account for the vessel icon
              fontSize: '0.85rem',
              '&::after': { // Arrow pointing to the vessel
                content: '""',
                position: 'absolute',
                bottom: '-8px',
                left: '50%',
                marginLeft: '-8px',
                borderWidth: '8px 8px 0',
                borderStyle: 'solid',
                borderColor: 'white transparent transparent',
                zIndex: 1
              }
            }}
          >
            <CardContent sx={{ p: 1.5, pb: 1.5 }}>
              <Button 
                sx={{ position: 'absolute', top: 2, right: 2, minWidth: 'auto', p: 0.5 }}
                onClick={() => setSelectedVessel(null)}
              >
                ×
              </Button>
              
              <Typography variant="subtitle1" sx={{ mb: 0.5, pr: 3, fontSize: '0.95rem' }}>
                {selectedVessel.name} ({selectedVessel.id})
              </Typography>
              
              <Box sx={{ mb: 0.25, fontSize: '0.8rem' }}>
                <Typography component="span" fontWeight="bold" fontSize="inherit">Status: </Typography>
                <Typography 
                  component="span" 
                  fontSize="inherit"
                  color={
                    selectedVessel.status === 'Operational' ? 'success.main' :
                    selectedVessel.status === 'Damaged' ? 'error.main' :
                    selectedVessel.status === 'Awaiting Tow' ? 'warning.main' :
                    selectedVessel.status === 'Diverted' ? 'info.main' :
                    'text.primary'
                  }
                >
                  {selectedVessel.status}
                </Typography>
              </Box>
              
              <Box sx={{ mb: 0.25, fontSize: '0.8rem' }}>
                <Typography component="span" fontWeight="bold" fontSize="inherit">Location: </Typography>
                <Typography component="span" fontSize="inherit">
                  {selectedVessel.location.lat.toFixed(4)}, {selectedVessel.location.lng.toFixed(4)}
                </Typography>
              </Box>
              
              <Box sx={{ mb: 0.25, fontSize: '0.8rem' }}>
                <Typography component="span" fontWeight="bold" fontSize="inherit">Last Check-In: </Typography>
                <Typography component="span" fontSize="inherit">{selectedVessel.lastCheckIn}</Typography>
              </Box>
              
              <Box sx={{ mb: 0.25, fontSize: '0.8rem' }}>
                <Typography component="span" fontWeight="bold" fontSize="inherit">Safe Harbor: </Typography>
                <Typography component="span" fontSize="inherit">{selectedVessel.safeHarbor}</Typography>
              </Box>
            </CardContent>
          </Card>
        )}
      </Paper>
      
      {/* Statistics and Legend */}
      <Paper sx={{ p: { xs: 1, sm: 2 }, mb: 2 }}>
        {/* For larger screens */}
        <Typography 
          variant="body2" 
          sx={{ 
            mb: 1, 
            display: { xs: 'none', sm: 'block' } 
          }}
        >
          Total Reports: {reports.length} | Tsunami Data Points: {TSUNAMI_DATA.length} | 
          Vessels: {VESSEL_DATA.length} | Airports: {PHILIPPINE_AIRPORTS.length} | 
          Cities: {CITIES.length} | Ports: {PORTS.length} | Click on a point to see more details
        </Typography>
        
        {/* For mobile screens */}
        <Typography 
          variant="body2" 
          sx={{ 
            mb: 1, 
            display: { xs: 'block', sm: 'none' },
            fontSize: '0.75rem'
          }}
        >
          Reports: {reports.length} | Data Points: {TSUNAMI_DATA.length}
        </Typography>
        
        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 2 }, flexWrap: 'wrap' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Box sx={{ width: 16, height: 16, bgcolor: '#4CAF50', borderRadius: '50%', mr: 1 }} />
            <Typography variant="body2">Verified Reports</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Box sx={{ width: 16, height: 16, bgcolor: '#FF9800', borderRadius: '50%', mr: 1 }} />
            <Typography variant="body2">Unverified Reports</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Box sx={{ width: 16, height: 16, bgcolor: '#3498db', borderRadius: '50%', mr: 1 }} />
            <Typography variant="body2">Tsunami Detection (small wave)</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Box sx={{ width: 16, height: 16, bgcolor: '#e74c3c', borderRadius: '50%', mr: 1 }} />
            <Typography variant="body2">Tsunami Detection (large wave)</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Box sx={{ width: 16, height: 3, bgcolor: 'rgba(0, 128, 0, 0.8)', mr: 1, border: '1px solid rgba(0, 128, 0, 1)' }} />
            <Typography variant="body2">250 km / 30 minutes</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Box sx={{ width: 16, height: 3, bgcolor: 'rgba(255, 255, 0, 0.8)', mr: 1, border: '1px solid rgba(255, 255, 0, 1)' }} />
            <Typography variant="body2">375 km / 45 minutes</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Box sx={{ width: 16, height: 3, bgcolor: 'rgba(255, 0, 0, 0.8)', mr: 1, border: '1px solid rgba(255, 0, 0, 1)' }} />
            <Typography variant="body2">500 km / 1 hour</Typography>
          </Box>
        </Box>
        
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', mt: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Box sx={{
      width: 0,
      height: 0,
      borderLeft: '8px solid transparent',  // Left side of the triangle, transparent
      borderRight: '8px solid transparent', // Right side of the triangle, transparent
      borderBottom: '16px solid #2ecc71',   // Bottom side of the triangle, colored
    }} />
            <Typography variant="body2">Operational Vessels</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Box sx={{
      width: 0,
      height: 0,
      borderLeft: '8px solid transparent',  // Left side of the triangle, transparent
      borderRight: '8px solid transparent', // Right side of the triangle, transparent
      borderBottom: '16px solid #e74c3c',   // Bottom side of the triangle, colored
    }} />
            <Typography variant="body2">Damaged Vessels</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Box sx={{
      width: 0,
      height: 0,
      borderLeft: '8px solid transparent',  // Left side of the triangle, transparent
      borderRight: '8px solid transparent', // Right side of the triangle, transparent
      borderBottom: '16px solid #f39c12',   // Bottom side of the triangle, colored
    }} /> 
            <Typography variant="body2">Vessels Awaiting Tow</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Box sx={{
      width: 0,
      height: 0,
      borderLeft: '8px solid transparent',  // Left side of the triangle, transparent
      borderRight: '8px solid transparent', // Right side of the triangle, transparent
      borderBottom: '16px solid #3498db',   // Bottom side of the triangle, colored
    }} />
            <Typography variant="body2">Diverted Vessels</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Box sx={{
      width: 0,
      height: 0,
      borderLeft: '8px solid transparent',  // Left side of the triangle, transparent
      borderRight: '8px solid transparent', // Right side of the triangle, transparent
      borderBottom: '16px solid #9b59b6',   // Bottom side of the triangle, colored
    }} />
            <Typography variant="body2">Anchored Vessels</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Box sx={{
      width: 0,
      height: 0,
      borderLeft: '8px solid transparent',  // Left side of the triangle, transparent
      borderRight: '8px solid transparent', // Right side of the triangle, transparent
      borderBottom: '16px solid #2ecc71',   // Bottom side of the triangle, colored
    }} />
            <Typography variant="body2">Vessels In Transit</Typography>
          </Box>
          
        </Box>
      </Paper>
      
      {/* Map Controls Panel */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="h6" gutterBottom>
          Map Controls
        </Typography>
        
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" gutterBottom>
            Go to Predefined Location:
          </Typography>
          <ButtonGroup variant="outlined" size="small">
            {Object.entries(LOCATIONS).map(([key, location]) => (
              <Button 
                key={key}
                onClick={() => flyToPredefinedLocation(key)}
              >
                {location.label}
              </Button>
            ))}
            <Button
              onClick={flyToTsunamiEpicenter}
              color="secondary"
            >
              Tsunami Epicenter
            </Button>
          </ButtonGroup>
        </Box>
        
        <Box sx={{ mb: 3, display: 'flex', flexWrap: 'wrap', gap: 2 }}>
          <Typography variant="subtitle2" sx={{ width: '100%' }}>
            Go to Custom Location:
          </Typography>
          <TextField
            label="Latitude"
            type="number"
            size="small"
            value={customLat}
            onChange={(e) => setCustomLat(e.target.value)}
            placeholder="e.g., 51.5074"
            inputProps={{ step: 'any' }}
          />
          <TextField
            label="Longitude"
            type="number"
            size="small"
            value={customLng}
            onChange={(e) => setCustomLng(e.target.value)}
            placeholder="e.g., -0.1278"
            inputProps={{ step: 'any' }}
          />
          <TextField
            label="Zoom"
            type="number"
            size="small"
            value={customZoom}
            onChange={(e) => setCustomZoom(e.target.value)}
            inputProps={{ min: 0, max: 22, step: 0.1 }}
            sx={{ width: 100 }}
          />
          <Button 
            variant="contained" 
            onClick={() => {
              const lat = parseFloat(customLat);
              const lng = parseFloat(customLng);
              const zoom = parseFloat(customZoom);
              
              if (!isNaN(lat) && !isNaN(lng) && !isNaN(zoom)) {
                flyToLocation(lng, lat, zoom);
              }
            }}
            disabled={!customLat || !customLng}
          >
            Go to Location
          </Button>
          <Tooltip title="Refresh Map Data">
            <Button 
              variant="contained" 
              size="small"
              onClick={handleRefresh}
              sx={{ 
                minWidth: 'auto', 
                px: 2,
                display: 'flex',
                alignItems: 'center', 
                gap: 0.5
              }}
            >
              <RefreshIcon fontSize="small" />
              <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                Refresh
              </Box>
            </Button>
          </Tooltip>
        </Box>
      </Paper>
      
      {/* Reports Table with Filters */}
      <Paper sx={{ p: { xs: 1, sm: 2 }, mb: 3 }}>
        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexDirection: { xs: 'column', sm: 'row' }, gap: 1 }}>
          <Typography variant="h6">
            Tsunami Reports {timeframe && `(${timeframe})`}
          </Typography>
          
          <Tooltip title="Refresh Data">
            <Button 
              variant="contained" 
              color="primary"
              onClick={handleRefresh}
              disabled={loading}
              size="small"
              sx={{ 
                minWidth: 'auto', 
                px: 2,
                display: 'flex',
                alignItems: 'center', 
                gap: 0.5
              }}
            >
              {loading ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                <>
                  <RefreshIcon fontSize="small" />
                  <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                    Refresh
                  </Box>
                </>
              )}
            </Button>
          </Tooltip>
        </Box>
        
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        {/* Filters Section */}
        <Accordion 
          expanded={filtersExpanded}
          onChange={() => setFiltersExpanded(!filtersExpanded)}
          sx={{ mb: 2 }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="filter-panel-content"
            id="filter-panel-header"
          >
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <FilterListIcon sx={{ mr: 1 }} />
              <Typography>Filters</Typography>
              {(filters.searchText || 
                filters.verified !== 'all' || 
                filters.hasImage !== 'all' || 
                filters.severity !== 'all' || 
                filters.dateRange.startDate || 
                filters.dateRange.endDate ||
                filters.categories.length > 0) && (
                <Chip 
                  label="Active" 
                  color="primary" 
                  size="small" 
                  sx={{ ml: 1 }}
                />
              )}
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={2}>
              {/* Search Filter */}
              <Grid item xs={12} md={4}>
                <TextField
                  label="Search"
                  placeholder="Search in title, description, ID..."
                  fullWidth
                  value={filters.searchText}
                  onChange={(e) => handleFilterChange('searchText', e.target.value)}
                  size="small"
                />
              </Grid>
              
              {/* Verification Status Filter */}
              <Grid item xs={12} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel id="verified-filter-label">Verification</InputLabel>
                  <Select
                    labelId="verified-filter-label"
                    value={filters.verified}
                    label="Verification"
                    onChange={(e) => handleFilterChange('verified', e.target.value)}
                  >
                    <MenuItem value="all">All</MenuItem>
                    <MenuItem value="verified">Verified</MenuItem>
                    <MenuItem value="unverified">Unverified</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              {/* Has Image Filter */}
              <Grid item xs={12} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel id="has-image-filter-label">Image</InputLabel>
                  <Select
                    labelId="has-image-filter-label"
                    value={filters.hasImage}
                    label="Image"
                    onChange={(e) => handleFilterChange('hasImage', e.target.value)}
                  >
                    <MenuItem value="all">All</MenuItem>
                    <MenuItem value="has_image">Has Image</MenuItem>
                    <MenuItem value="no_image">No Image</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              {/* Severity Filter */}
              <Grid item xs={12} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel id="severity-filter-label">Severity</InputLabel>
                  <Select
                    labelId="severity-filter-label"
                    value={filters.severity}
                    label="Severity"
                    onChange={(e) => handleFilterChange('severity', e.target.value)}
                  >
                    <MenuItem value="all">All</MenuItem>
                    {severityOptions.map((severity) => (
                      <MenuItem key={severity} value={severity}>{severity}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              {/* Reset Filters Button */}
              <Grid item xs={12} md={2}>
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={resetFilters}
                  disabled={
                    !filters.searchText && 
                    filters.verified === 'all' && 
                    filters.hasImage === 'all' && 
                    filters.severity === 'all' && 
                    !filters.dateRange.startDate && 
                    !filters.dateRange.endDate &&
                    filters.categories.length === 0
                  }
                >
                  Reset Filters
                </Button>
              </Grid>
              
              {/* Date Range Filter */}
              <Grid item xs={12} md={6}>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <TextField
                      label="Start Date"
                      type="date"
                      fullWidth
                      value={filters.dateRange.startDate}
                      onChange={(e) => handleDateRangeChange('startDate', e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      label="End Date"
                      type="date"
                      fullWidth
                      value={filters.dateRange.endDate}
                      onChange={(e) => handleDateRangeChange('endDate', e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      size="small"
                    />
                  </Grid>
                </Grid>
              </Grid>
              
              {/* Categories Filter */}
              {availableCategories.length > 0 && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" gutterBottom>Categories</Typography>
                  <FormGroup row>
                    {availableCategories.map(category => (
                      <FormControlLabel
                        key={category}
                        control={
                          <Checkbox 
                            checked={filters.categories.includes(category)} 
                            onChange={() => handleCategoryChange(category)}
                            size="small"
                          />
                        }
                        label={<Typography variant="body2">{category}</Typography>}
                        sx={{ width: 'auto', mr: 2 }}
                      />
                    ))}
                  </FormGroup>
                </Grid>
              )}
            </Grid>
          </AccordionDetails>
        </Accordion>
        
        {/* Filter Summary */}
        {(filters.searchText || 
          filters.verified !== 'all' || 
          filters.hasImage !== 'all' || 
          filters.severity !== 'all' || 
          filters.dateRange.startDate || 
          filters.dateRange.endDate ||
          filters.categories.length > 0) && (
          <Box sx={{ mb: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {filters.searchText && (
              <Chip 
                label={`Search: ${filters.searchText}`} 
                onDelete={() => handleFilterChange('searchText', '')}
                size="small"
              />
            )}
            
            {filters.verified !== 'all' && (
              <Chip 
                label={`Status: ${filters.verified === 'verified' ? 'Verified' : 'Unverified'}`} 
                onDelete={() => handleFilterChange('verified', 'all')}
                size="small"
              />
            )}
            
            {filters.hasImage !== 'all' && (
              <Chip 
                label={`Image: ${filters.hasImage === 'has_image' ? 'Yes' : 'No'}`} 
                onDelete={() => handleFilterChange('hasImage', 'all')}
                size="small"
              />
            )}
            
            {filters.severity !== 'all' && (
              <Chip 
                label={`Severity: ${filters.severity}`} 
                onDelete={() => handleFilterChange('severity', 'all')}
                size="small"
              />
            )}
            
            {filters.dateRange.startDate && (
              <Chip 
                label={`From: ${filters.dateRange.startDate}`} 
                onDelete={() => handleDateRangeChange('startDate', '')}
                size="small"
              />
            )}
            
            {filters.dateRange.endDate && (
              <Chip 
                label={`To: ${filters.dateRange.endDate}`} 
                onDelete={() => handleDateRangeChange('endDate', '')}
                size="small"
              />
            )}
            
            {filters.categories.map(category => (
              <Chip 
                key={category}
                label={`Category: ${category}`} 
                onDelete={() => handleCategoryChange(category)}
                size="small"
              />
            ))}
          </Box>
        )}
        
        {/* Make the table responsive */}
        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table sx={{ minWidth: { xs: 500, sm: 650 } }} size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>ID</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>Date</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap', display: { xs: 'none', sm: 'table-cell' } }}>Location</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap', display: { xs: 'none', md: 'table-cell' } }}>Severity</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>Verified</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap', display: { xs: 'none', md: 'table-cell' } }}>Image</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap', display: { xs: 'none', lg: 'table-cell' } }}>Categories</TableCell>
                <TableCell>Description</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredReports.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((report) => (
                <TableRow 
                  key={report.id} 
                  hover 
                  onClick={() => handleRowClick(report)}
                  sx={{ 
                    cursor: 'pointer',
                    ...(report.status === "SOS" && {
                      backgroundColor: 'error.light',
                      '&:hover': {
                        backgroundColor: 'error.main',
                        color: 'white'
                      }
                    })
                  }}
                >
                  <TableCell component="th" scope="row" sx={{ whiteSpace: 'nowrap' }}>
                    {report.id.substring(0, 8)}...
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDate(report.reported_at)}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap', display: { xs: 'none', sm: 'table-cell' } }}>
                    {report.latitude && report.longitude 
                      ? `${report.latitude.toFixed(4)}, ${report.longitude.toFixed(4)}`
                      : 'N/A'
                    }
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap', display: { xs: 'none', md: 'table-cell' } }}>{report.severity || 'N/A'}</TableCell>
                  <TableCell>
                    {report.status === "SOS" ? (
                      <Chip 
                        label="SOS" 
                        color="error"
                        size="small"
                        sx={{ fontWeight: 'bold' }}
                      />
                    ) : (
                      <Chip 
                        label={report.verified ? 'Yes' : 'No'} 
                        color={report.verified ? 'success' : 'default'}
                        size="small"
                      />
                    )}
                  </TableCell>
                  <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                    {report.image_urls && report.image_urls.length > 0 ? (
                      <ImageIcon color="primary" fontSize="small" />
                    ) : ''}
                  </TableCell>
                  <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>
                    {report.ai_categories && report.ai_categories.length > 0 ? (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, maxWidth: 200 }}>
                        {report.ai_categories.slice(0, 2).map((category, index) => (
                          <Chip 
                            key={index}
                            label={category.category}
                            size="small"
                            variant="outlined"
                            sx={{ fontSize: '0.7rem' }}
                          />
                        ))}
                        {report.ai_categories.length > 2 && (
                          <Chip 
                            label={`+${report.ai_categories.length - 2}`}
                            size="small"
                            variant="outlined"
                            sx={{ fontSize: '0.7rem' }}
                          />
                        )}
                      </Box>
                    ) : (
                      'None'
                    )}
                  </TableCell>
                  <TableCell>
                    {(report.original_description || report.description || report.text || '').substring(0, 30)}
                    {(report.original_description || report.description || report.text || '').length > 30 ? '...' : ''}
                  </TableCell>
                </TableRow>
              ))}
              {loading && (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              )}
              {!loading && filteredReports.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    {reports.length > 0 ? 'No reports match the current filters' : 'No reports found'}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={count}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          sx={{
            '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': {
              fontSize: { xs: '0.75rem', sm: '0.875rem' }
            }
          }}
        />
      </Paper>

      {/* Vessels Information Table */}
      <Paper sx={{ p: { xs: 1, sm: 2 }, mb: 3 }}>
        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexDirection: { xs: 'column', sm: 'row' }, gap: 1 }}>
          <Typography variant="h6">
            Vessel Information
          </Typography>
          
          <Button 
            variant="contained" 
            color={showVessels ? "primary" : "secondary"}
            onClick={toggleVessels}
            fullWidth={{ xs: true, sm: false }}
          >
            {showVessels ? "Hide Vessels on Map" : "Show Vessels on Map"}
          </Button>
        </Box>
        
        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table sx={{ minWidth: { xs: 500, sm: 650 } }} size="small">
            <TableHead>
              <TableRow>
                <TableCell>Vessel ID</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Location</TableCell>
                <TableCell>Last Check-In</TableCell>
                <TableCell>Safe Harbor</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {VESSEL_DATA.map((vessel) => (
                <TableRow 
                  key={vessel.id} 
                  hover 
                  onClick={() => {
                    if (map) {
                      map.flyTo({
                        center: [vessel.location.lng, vessel.location.lat],
                        zoom: 10,
                        essential: true,
                        duration: 2000
                      });
                      
                      // Ensure vessels are visible on the map
                      if (!showVessels) {
                        setShowVessels(true);
                        map.setLayoutProperty('vessels-layer', 'visibility', 'visible');
                      }
                    }
                  }}
                  sx={{ 
                    cursor: 'pointer',
                    '&:hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0.04)'
                    }
                  }}
                >
                  <TableCell>{vessel.id}</TableCell>
                  <TableCell>{vessel.name}</TableCell>
                  <TableCell>
                    <Chip 
                      label={vessel.status}
                      size="small"
                      sx={{
                        backgroundColor: 
                          vessel.status === 'Operational' ? '#2ecc71' :
                          vessel.status === 'Damaged' ? '#e74c3c' :
                          vessel.status === 'Awaiting Tow' ? '#f39c12' :
                          vessel.status === 'Diverted' ? '#3498db' :
                          vessel.status === 'Anchored' ? '#9b59b6' :
                          vessel.status === 'In Transit' ? '#1abc9c' :
                          '#95a5a6',
                        color: '#fff'
                      }}
                    />
                  </TableCell>
                  <TableCell>{vessel.location.lat.toFixed(4)}, {vessel.location.lng.toFixed(4)}</TableCell>
                  <TableCell>{vessel.lastCheckIn}</TableCell>
                  <TableCell>{vessel.safeHarbor}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        
        <Box sx={{ mt: 2, fontSize: '0.85rem', color: 'text.secondary' }}>
          <Typography variant="body2">
            Click on any vessel row to center the map on that vessel's location
          </Typography>
        </Box>
      </Paper>

      {/* Report Detail Dialog */}
      <Dialog
        open={detailDialogOpen}
        onClose={handleCloseDetailDialog}
        fullWidth
        maxWidth="md"
        fullScreen={{ xs: true, sm: false }}
      >
        {selectedReport && (
          <>
            <DialogTitle sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              p: { xs: 1, sm: 2 },
              ...(selectedReport.status === "SOS" && {
                backgroundColor: 'error.main',
                color: 'white'
              })
            }}>
              <Typography variant="h6" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                {selectedReport.title || 'Untitled Report'} 
                <Typography component="span" variant="subtitle1" sx={{ 
                  ml: 1, 
                  color: selectedReport.status === "SOS" ? 'white' : 'text.secondary',
                  fontSize: { xs: '0.8rem', sm: '1rem' }
                }}>
                  ({selectedReport.id})
                </Typography>
                {selectedReport.status === "SOS" && (
                  <Chip 
                    label="SOS" 
                    color="error" 
                    size="small" 
                    sx={{ ml: 2, fontWeight: 'bold', border: '1px solid white' }}
                  />
                )}
              </Typography>
              <IconButton onClick={handleCloseDetailDialog} size="small">
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent dividers sx={{ p: { xs: 1, sm: 2 } }}>
              <Grid container spacing={3}>
                {/* Images Section */}
                {selectedReport.image_urls && selectedReport.image_urls.length > 0 && (
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle1" gutterBottom>Images</Typography>
                    <Grid container spacing={2}>
                      {selectedReport.image_urls.map((url, index) => (
                        <Grid item xs={12} sm={6} key={index}>
                          <Card>
                            <CardMedia
                              component="img"
                              image={`${apiBaseUrl}${url}`}
                              alt={`Report image ${index + 1}`}
                              sx={{ height: 240, objectFit: 'contain' }}
                            />
                          </Card>
                        </Grid>
                      ))}
                    </Grid>
                  </Grid>
                )}

                {/* Primary Information */}
                <Grid item xs={12} md={selectedReport.image_urls && selectedReport.image_urls.length > 0 ? 6 : 12}>
                  <Typography variant="subtitle1" gutterBottom>Report Information</Typography>
                  <List dense>
                    <ListItem>
                      <ListItemText 
                        primary="Reported At" 
                        secondary={formatDate(selectedReport.reported_at)}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText 
                        primary="Created At" 
                        secondary={formatDate(selectedReport.created_at)}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText 
                        primary="Location" 
                        secondary={
                          selectedReport.latitude && selectedReport.longitude
                            ? `${selectedReport.latitude}, ${selectedReport.longitude}`
                            : 'N/A'
                        }
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText 
                        primary="Location Description" 
                        secondary={selectedReport.location_description || selectedReport.location_name || 'N/A'}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText 
                        primary="Severity" 
                        secondary={selectedReport.severity || 'N/A'}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText 
                        primary="Verified" 
                        secondary={
                          selectedReport.status === "SOS" ? (
                            <Chip 
                              label="SOS" 
                              color="error"
                              size="small"
                              sx={{ fontWeight: 'bold' }}
                            />
                          ) : (
                            <Chip 
                              label={selectedReport.verified ? 'Yes' : 'No'} 
                              color={selectedReport.verified ? 'success' : 'default'}
                              size="small"
                            />
                          )
                        }
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText 
                        primary="Description" 
                        secondary={selectedReport.original_description || selectedReport.description || selectedReport.text || 'N/A'}
                      />
                    </ListItem>
                    {selectedReport.has_damages && selectedReport.damage_details && (
                      <ListItem>
                        <ListItemText 
                          primary="Damage Details" 
                          secondary={selectedReport.damage_details || 'N/A'}
                        />
                      </ListItem>
                    )}
                  </List>
                </Grid>

                {/* Additional Data Section */}
                {selectedReport.ai_categories && (
                  <Grid item xs={12}>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="subtitle1" gutterBottom>AI Categories</Typography>
                    <Grid container spacing={1}>
                      {selectedReport.ai_categories.map((category, index) => (
                        <Grid item key={index}>
                          <Chip 
                            label={`${category.category} (${category.score})`}
                            variant="outlined"
                            size="small"
                          />
                        </Grid>
                      ))}
                    </Grid>
                  </Grid>
                )}

                {/* Tsunami Impact Information */}
                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle1" gutterBottom>Tsunami Impact</Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={4}>
                      <List dense>
                        <ListItem>
                          <ListItemText 
                            primary="Wave Height (meters)" 
                            secondary={selectedReport.height_meters || 'N/A'}
                          />
                        </ListItem>
                      </List>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <List dense>
                        <ListItem>
                          <ListItemText 
                            primary="Damage Estimate (USD)" 
                            secondary={selectedReport.damage_estimated_usd ? `$${selectedReport.damage_estimated_usd}` : 'N/A'}
                          />
                        </ListItem>
                      </List>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <List dense>
                        <ListItem>
                          <ListItemText 
                            primary="Casualties" 
                            secondary={selectedReport.casualties || 'N/A'}
                          />
                        </ListItem>
                      </List>
                    </Grid>
                  </Grid>
                </Grid>

                {/* Raw Data Section */}
                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle1" gutterBottom>Photo Description</Typography>
                  <Box 
                    component="pre" 
                    sx={{ 
                      p: 2, 
                      backgroundColor: 'black.100', 
                      overflowX: 'auto',
                      fontSize: '0.75rem',
                      borderRadius: 1
                    }}
                  >
                    {formatJsonData(selectedReport.photo_description)}
                  </Box>
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions sx={{ p: { xs: 1, sm: 2 } }}>
              <Button onClick={handleCloseDetailDialog}>Close</Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
} 